import { EmployeeTrainingsApi } from "@simbelapp/order-sdk";
import { defineStore } from "pinia";
import { useSDKApi } from "~/composables/api/useSDKApi";
import { MicroserviceEnum } from "~/utils/enums/common.enums";
import { usePagination } from "~/composables/paginations/usePagination";
import type { IEmployeeTrainingsStore } from "~/utils/interfaces/employee-trainings";

export const useEmployeeTrainingsStore = defineStore("employeeTrainings", {
  state: (): IEmployeeTrainingsStore => {
    return {
      allEmployeeTrainings: null,
      allEmployeeTrainingsHome: null,
      employeeTraining: null,
      homePageEmployeeTrainings: [],

      showMySessionModal: false,
      showMyRequestModal: false,
      showEmployeeBookingDetails: false,
      offset: 0,
      maxOffset: 0,
      selectedCategoryTab: null,
      employeeTrainingFetchLoading: false,
      scrollFetchLoading: false,
      homeEmployeeTrainingFetchLoading: false,
      showElectronicSignatureModal: false,
    };
  },
  getters: {},
  actions: {
    softReset() {
      this.employeeTraining = null;
    },
    resetPaginate() {
      const paginate = usePagination();
      if (this.allEmployeeTrainings) {
        this.allEmployeeTrainings.data = [];
      }

      this.offset = 0;
      this.maxOffset = 0;
      paginate.paginateOptions.value.offset = 0;
    },
    async fetchAllEmployeeTrainings(noFilter?: boolean, fetchByScroll?: boolean) {
      // Prevent duplicate fetches
      if (fetchByScroll) {
        // For scroll fetches, use scrollFetchLoading (shows small loader at bottom)
        if (this.scrollFetchLoading) {
          return;
        }
        this.scrollFetchLoading = true;
      } else {
        // For initial/filter loads, use state flag (shows full loading UI)
        if (this.employeeTrainingFetchLoading) {
          return;
        }
        this.employeeTrainingFetchLoading = true;
      }

      try {
        const paginate = usePagination();

        // Reset offset to 0 only if NOT fetching by scroll (initial load or filter change)
        if (!fetchByScroll) {
          paginate.paginateOptions.value.offset = 0;
          this.offset = 0;
        }

        const paginateOptions = paginate.getFindOptions(paginate.paginateOptions.value);
        const paginateQuery = new URLSearchParams(paginateOptions);
        const queryString = Object.fromEntries(paginateQuery.entries());
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, EmployeeTrainingsApi);
        const data = await apiInstance.getAllEmployeeTrainings({
          ...(!noFilter && { ...queryString }),
          sortingColumn: paginateOptions.sortingColumn,
        });
        if (data) {
          // Update maxOffset from response
          const totalCount = data.meta?.totalCount;
          if (totalCount) {
            // Calculate max based on filter type
            const filter = paginate.paginateOptions.value.filter;
            if (filter === "request") {
              this.maxOffset = totalCount.request || 0;
            } else {
              this.maxOffset = (totalCount.ended || 0) + (totalCount.ongoing || 0) + (totalCount.upcoming || 0);
            }
          }

          if (fetchByScroll && this.allEmployeeTrainings?.data && data.data) {
            // Append data when scrolling
            this.allEmployeeTrainings = {
              ...data,
              data: [...this.allEmployeeTrainings.data, ...data.data],
            };
            // Update offset by adding new items count
            this.offset += data.data.length;
          } else {
            // Replace data on initial load or filter change
            this.allEmployeeTrainings = data;
            // Set offset to number of items loaded
            this.offset = data.data?.length || 0;
          }

          // Update paginate offset for next API call
          paginate.paginateOptions.value.offset = this.offset;
        }
      } catch {
        if (!fetchByScroll) {
          this.allEmployeeTrainings = null;
        }
      } finally {
        if (fetchByScroll) {
          this.scrollFetchLoading = false;
        } else {
          this.employeeTrainingFetchLoading = false;
        }
      }
    },

    async fetchEmployeeTraining(myTrainingId: string) {
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, EmployeeTrainingsApi);
        const data = await apiInstance.getEmployeeTraining({ myTrainingId });
        if (data) {
          this.employeeTraining = data;
        }
      } catch {
        this.employeeTraining = null;
      }
    },

    async fetchHomePageEmployeeTrainings() {
      try {
        this.homeEmployeeTrainingFetchLoading = true;
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, EmployeeTrainingsApi);
        const data = await apiInstance.getHomePageEmployeeTrainings();
        if (data) {
          this.allEmployeeTrainingsHome = {
            data,
            meta: null,
          };
        }
      } catch {
        this.allEmployeeTrainingsHome = null;
      } finally {
        this.homeEmployeeTrainingFetchLoading = false;
      }
    },
  },
});
